import React, { useState, useRef } from "react";
import { Button, Tabs, Tag } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import PageHeader from "../../shared/PageHeader";
import issuedRawReport from "./issued-raw-report.json";
import bookingWise from "./booking-wise.json";
import cardChargedRawData from "./card-charged-raw-data.json";
import reservationData from "./reservation-data.json";
import AgTableClient from "../../shared/AgTable/AgTableClient";

// Helper function to format boolean values as text instead of checkboxes
const booleanValueFormatter = (params) => {
  const value = params?.value ?? params;
  if (value === true || value === "true" || value === "True" || value === 1) {
    return "Yes";
  }
  if (value === false || value === "false" || value === "False" || value === 0) {
    return "No";
  }
  return value !== null && value !== undefined ? String(value) : "";
};

// Cell renderer to prevent checkboxes for boolean values
const booleanCellRenderer = (params) => {
  return booleanValueFormatter(params);
};

// Cell renderer for Purchase Request Status with colored tags
const statusCellRenderer = (params) => {
  const status = params?.value || 
                 params?.data?.["Purchase Request Status"] || 
                 params?.data?.["Issue Purchase Request Status"] || 
                 "";
  const statusColors = {
    "Cancelled": "red",
    "Approved": "green",
    "Declined": "orange",
    "Blocked": "volcano"
  };
  const color = statusColors[status] || "default";
  return <Tag color={color}>{status}</Tag>;
};

// Cell renderer for Yes/No columns with icons
const yesNoIconRenderer = (params) => {
  // If it's a grouped row, return text instead of icon
  if (params.node && params.node.group) {
    const value = params?.value ?? "";
    const normalizedValue = String(value).toLowerCase().trim();
    
    // Return "Yes" or "No" text for grouped rows
    if (
      normalizedValue === "yes" || 
      normalizedValue === "true" || 
      normalizedValue === "1" ||
      value === true ||
      value === 1
    ) {
      return "Yes";
    } else if (
      normalizedValue === "no" || 
      normalizedValue === "false" || 
      normalizedValue === "0" ||
      value === false ||
      value === 0
    ) {
      return "No";
    }
    return value !== null && value !== undefined ? String(value) : "";
  }
  
  // For regular rows, show icons
  const value = params?.value ?? "";
  const normalizedValue = String(value).toLowerCase().trim();
  
  // Handle true/yes values
  if (
    normalizedValue === "yes" || 
    normalizedValue === "true" || 
    normalizedValue === "1" ||
    value === true ||
    value === 1
  ) {
  return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CheckCircleFilled style={{ color: "#43a522", fontSize: 14 }} />
      </div>
    );
  } 
  // Handle false/no values
  else if (
    normalizedValue === "no" || 
    normalizedValue === "false" || 
    normalizedValue === "0" ||
    value === false ||
    value === 0
  ) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CloseCircleFilled style={{ color: "#dd5061", fontSize: 14 }} />
    </div>
  );
  }
  return value;
};

// Helper function to create keyCreator for date columns (converts Date to string for grouping)
const createDateKeyCreator = (dateFormatter = (date) => date.toLocaleDateString("en-GB")) => {
  return (params) => {
    if (!params.value) return "";
    if (params.value instanceof Date) {
      return dateFormatter(params.value);
    }
    // If it's already a string, return it
    return String(params.value);
  };
};

// Helper function to create valueFormatter for date columns (handles both Date objects and grouped values)
const createDateValueFormatter = (dateFormatter = (date) => date.toLocaleDateString("en-GB")) => {
  return (params) => {
    if (!params.value) return "";
    // Handle Date objects
    if (params.value instanceof Date) {
      return dateFormatter(params.value);
    }
    // Handle grouped values (which might be Date objects or strings)
    if (typeof params.value === 'object' && params.value !== null) {
      try {
        const date = params.value instanceof Date ? params.value : new Date(params.value);
        return dateFormatter(date);
      } catch (e) {
        return String(params.value);
      }
    }
    return String(params.value);
  };
};

// Helper for dd-mm-yy format
const formatDateDDMMYY = (date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};


// Column definitions for Issued Raw Report
const columnDefsIssuedRawReport = [
  { field: "Purchase Request ID", headerName: "Purchase Request ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Request Date",
    headerName: "Request Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Request Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display (handles both Date objects and grouped values)
    valueFormatter: (params) => {
      if (!params.value) return "";
      // Handle Date objects
      if (params.value instanceof Date) {
        return params.value.toLocaleDateString("en-GB"); // 01/12/2025
      }
      // Handle grouped values (which might be Date objects or strings)
      if (typeof params.value === 'object' && params.value !== null) {
        try {
          const date = params.value instanceof Date ? params.value : new Date(params.value);
          return date.toLocaleDateString("en-GB");
        } catch (e) {
          return String(params.value);
        }
      }
      return String(params.value);
    },
  
    // 3️⃣ Key creator for grouping (returns string representation)
    keyCreator: (params) => {
      if (!params.value) return "";
      if (params.value instanceof Date) {
        return params.value.toLocaleDateString("en-GB");
      }
      return String(params.value);
    },
  
    // 4️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  
  { field: "Requestor", headerName: "Requestor", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Purchase Type", headerName: "Purchase Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Purchase Template", headerName: "Purchase Template", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { 
    field: "Billing Amount", 
    headerName: "Billing Amount", 
    filter: "agNumberColumnFilter", 
    enableRowGroup: true, 
    sortable: true,
    formatType: "AMOUNT"
  },
  { field: "Billing Currency Code", headerName: "Billing Currency Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Amount", headerName: "Merchant Amount", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Currency Code", headerName: "Merchant Currency Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { 
    field: "Purchase Request Status", 
    headerName: "Purchase Request Status", 
    filter: "agTextColumnFilter", 
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: statusCellRenderer
  },
  { field: "Real Card Alias", headerName: "Real Card Alias", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Real Card Number", headerName: "Real Card Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Virtual Card Number", headerName: "Virtual Card Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Validity (months)", headerName: "Validity (months)", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "VCN Expiry Date",
    headerName: "VCN Expiry Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["VCN Expiry Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "Velocity", headerName: "Velocity", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Time Period", headerName: "Time Period", filter: "agDateColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Cumulative Limit", headerName: "Cumulative Limit", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Maximum Number of Transactions", headerName: "Maximum Number of Transactions", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Available Balance", headerName: "Available Balance", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true,  formatType: "AMOUNT" },
  { field: "Validity", headerName: "Validity", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Start Date",
    headerName: "Start Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (required for agDateColumnFilter)
    valueGetter: (params) => {
      const value = params.data?.["Start Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Display as dd-mm-yy
    valueFormatter: (params) => {
      if (!params.value) return "";
  
      const d = params.value;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
  
      return `${day}/${month}/${year}`;
    },
  
    // 3️⃣ Date filter comparator (time ignored)
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  }, 
  {
    field: "End Date",
    headerName: "End Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (required for agDateColumnFilter)
    valueGetter: (params) => {
      const value = params.data?.["End Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Display as dd-mm-yy
    valueFormatter: (params) => {
      if (!params.value) return "";
  
      const d = params.value;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
  
      return `${day}/${month}/${year}`;
    },
  
    // 3️⃣ Date filter comparator (time ignored)
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  }, 
  { field: "Time Zone", headerName: "Time Zone", filter: "agDateColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Strict PreAuth Check",
    headerName: "Strict PreAuth Check",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      if (params.value === true || params.value === "true" || params.value === 1)
        return "Yes";
      if (params.value === false || params.value === "false" || params.value === 0)
        return "No";
      return "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["Yes", "No"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
  {
    field: "Negate",
    headerName: "Negate",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      if (params.value === true || params.value === "true" || params.value === 1)
        return "Yes";
      if (params.value === false || params.value === "false" || params.value === 0)
        return "No";
      return "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["Yes", "No"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
  { field: "Exact Amount Range", headerName: "Exact Amount Range", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Minimum Transaction Amount", headerName: "Minimum Transaction Amount", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Maximum Transaction Amount", headerName: "Maximum Transaction Amount", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Strict PreAuth Check1",
    headerName: "Strict PreAuth Check1",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      if (params.value === true || params.value === "true" || params.value === 1)
        return "Yes";
      if (params.value === false || params.value === "false" || params.value === 0)
        return "No";
      return "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["Yes", "No"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
  {
    field: "Negate1", 
    headerName: "Negate1", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Merchant ID", headerName: "Merchant ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant ID/Acquirer ID", headerName: "Merchant ID/Acquirer ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Negate2", 
    headerName: "Negate2", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Transaction Limit", headerName: "Transaction Limit", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Maximum Single Transaction Limit", headerName: "Maximum Single Transaction Limit", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Negate3", 
    headerName: "Negate3", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Time of Day", headerName: "Time of Day", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Day and Time", headerName: "Day and Time", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Time Zone1", headerName: "Time Zone1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Negate4", 
    headerName: "Negate4", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Travel", headerName: "Travel", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Maximum Cumulative Transaction Limit", headerName: "Maximum Cumulative Transaction Limit", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Available Balance1", headerName: "Available Balance1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Negate5", 
    headerName: "Negate5", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Geographic", headerName: "Geographic", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Country/Territory Code(s)", headerName: "Country/Territory Code(s)", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Negate6", 
    headerName: "Negate6", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: yesNoIconRenderer
  },
  { field: "Curfew", headerName: "Curfew", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Day and Time1", headerName: "Day and Time1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Time Zone2", headerName: "Time Zone2", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Negate7", headerName: "Negate7", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Aging Velocity", headerName: "Aging Velocity", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Authorization Hold Length (in Days)", headerName: "Authorization Hold Length (in Days)", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Cumulative Limit1", headerName: "Cumulative Limit1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Time Zone3", headerName: "Time Zone3", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Negate8", headerName: "Negate8", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Available Balance2", headerName: "Available Balance2", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Email 1", headerName: "Supplier Email 1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Email 2", headerName: "Supplier Email 2", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Email 3", headerName: "Supplier Email 3", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Email 4", headerName: "Supplier Email 4", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Email 5", headerName: "Supplier Email 5", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Name", headerName: "Supplier Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Name", headerName: "Merchant Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Name(s)", headerName: "Merchant Name(s)", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Negate9", headerName: "Negate9", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF1 Label", headerName: "CDF1 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF1 Value", headerName: "CDF1 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF2 Label", headerName: "CDF2 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF2 Value", headerName: "CDF2 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF3 Label", headerName: "CDF3 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF3 Value", headerName: "CDF3 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF4 Label", headerName: "CDF4 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF4 Value", headerName: "CDF4 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF5 Label", headerName: "CDF5 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF5 Value", headerName: "CDF5 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF6 Label", headerName: "CDF6 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF6 Value", headerName: "CDF6 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF7 Label", headerName: "CDF7 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF7 Value", headerName: "CDF7 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF8 Label", headerName: "CDF8 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF8 Value", headerName: "CDF8 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF9 Label", headerName: "CDF9 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF9 Value", headerName: "CDF9 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF10 Label", headerName: "CDF10 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF10 Value", headerName: "CDF10 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF11 Label", headerName: "CDF11 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF11 Value", headerName: "CDF11 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF12 Label", headerName: "CDF12 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF12 Value", headerName: "CDF12 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF13 Label", headerName: "CDF13 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF13 Value", headerName: "CDF13 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF14 Label", headerName: "CDF14 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF14 Value", headerName: "CDF14 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF15 Label", headerName: "CDF15 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF15 Value", headerName: "CDF15 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF16 Label", headerName: "CDF16 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF16 Value", headerName: "CDF16 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF17 Label", headerName: "CDF17 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF17 Value", headerName: "CDF17 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF18 Label", headerName: "CDF18 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF18 Value", headerName: "CDF18 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF19 Label", headerName: "CDF19 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF19 Value", headerName: "CDF19 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF20 Label", headerName: "CDF20 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF20 Value", headerName: "CDF20 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF21 Label", headerName: "CDF21 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF21 Value", headerName: "CDF21 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF22 Label", headerName: "CDF22 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF22 Value", headerName: "CDF22 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF23 Label", headerName: "CDF23 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF23 Value", headerName: "CDF23 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF24 Label", headerName: "CDF24 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF24 Value", headerName: "CDF24 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF25 Label", headerName: "CDF25 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF25 Value", headerName: "CDF25 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF26 Label", headerName: "CDF26 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF26 Value", headerName: "CDF26 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF27 Label", headerName: "CDF27 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF27 Value", headerName: "CDF27 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF28 Label", headerName: "CDF28 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF28 Value", headerName: "CDF28 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF29 Label", headerName: "CDF29 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF29 Value", headerName: "CDF29 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Enabled for mobile payments",
    headerName: "Enabled for mobile payments",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      if (params.value === true || params.value === "true" || params.value === 1)
        return "Yes";
      if (params.value === false || params.value === "false" || params.value === 0)
        return "No";
      return "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["Yes", "No"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
];

// Column definitions for Booking-wise
const columnDefsBookingWise = [
  { field: "Booking ID", headerName: "Booking ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Supplier Source", headerName: "Supplier Source", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Source Code", headerName: "Source Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Vendor Id", headerName: "Vendor Id", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "mib", headerName: "MIB", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "TBR", headerName: "TBR", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Booking Date",
    headerName: "Booking Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Booking Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "Travel Agent Name", headerName: "Travel Agent Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Check In Date",
    headerName: "Check In Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Check In Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  {
    field: "Check out Date",
    headerName: "Check out Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Check out Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "Hotel Name", headerName: "Hotel Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true, formatType:"ENTITY_WITH_LOGO" },
  { field: "City", headerName: "City", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Guest Name", headerName: "Guest Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Status", headerName: "Status", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Accountant Remarks", headerName: "Accountant Remarks", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payable to hotel", headerName: "Payable to Hotel", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true, formatType:"AMOUNT" },
  { field: "payment Class", headerName: "Payment Class", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Card_amount", headerName: "Card Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true,formatType:"AMOUNT" },
  // { 
  //   field: "Card Status", 
  //   headerName: "Card Status", 
  //   filter: "agTextColumnFilter",
  //   enableRowGroup: true, 
  //   sortable: true,
  //   valueFormatter: booleanValueFormatter,
  //   cellRenderer: booleanCellRenderer
  // },
  {
    field: "Card Status",
    headerName: "Card Status",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      const value = params.value;
      const normalizedValue = String(value).toLowerCase().trim();
      
      // Handle true/yes values (case-insensitive)
      if (
        value === true || 
        value === 1 || 
        normalizedValue === "true" || 
        normalizedValue === "yes" ||
        normalizedValue === "1"
      ) {
        return "Yes";
      }
      
      // Handle false/no values (case-insensitive)
      if (
        value === false || 
        value === 0 || 
        normalizedValue === "false" || 
        normalizedValue === "no" ||
        normalizedValue === "0"
      ) {
        return "No";
      }
      
      return value !== null && value !== undefined ? String(value) : "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["TRUE", "FALSE"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
  { field: "Virtual Credit Card Number", headerName: "Virtual Credit Card Number", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Transaction Number", headerName: "Transaction Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Purchase Resquest ID", headerName: "Purchase Request ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "cancelled_bnkg/UTR", headerName: "Cancelled Booking/UTR", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Cancel date", headerName: "Cancel Date", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "alternate_purchase_id", headerName: "Alternate Purchase ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "re_issued_purchase_id_1", headerName: "Re-issued Purchase ID 1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "re_issued_purchase_id_2", headerName: "Re-issued Purchase ID 2", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "axis_charged_amount", headerName: "Axis Charged Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Issue Billing Amount", headerName: "Issue Billing Amount", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "citi_charged_amount", headerName: "Citi Charged Amount", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Difference_amount", headerName: "Difference Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Start Date", headerName: "Start Date", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "End Date", headerName: "End Date", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "neft_payment_tg_with_adj", headerName: "NEFT Payment TG with Adj", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "neft_payment_corp_adj", headerName: "NEFT Payment Corp Adj", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "loss_neft", headerName: "Loss NEFT", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "diff_with_adj", headerName: "Diff with Adj", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "start_date_alt_purchase_id_", headerName: "Start Date Alt Purchase ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "end_date_alt_purchase_id_", headerName: "End Date Alt Purchase ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "start_date_re_issued_1", headerName: "Start Date Re-issued 1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "end_date_re_issued_1", headerName: "End Date Re-issued 1", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "remarks_short", headerName: "Remarks Short", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "final_remarks", headerName: "Final Remarks", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Charged Purchase Request ID", headerName: "Charged Purchase Request ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Issuer Response", headerName: "Issuer Response", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { 
    field: "Purchase Request Status", 
    headerName: "Purchase Request Status", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: statusCellRenderer
  },
  { field: "Settled", headerName: "Settled", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Issue Available Balance", headerName: "Issue Available Balance", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Issue Purchase Request Status", 
    headerName: "Issue Purchase Request Status", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: statusCellRenderer
  },
  { field: "Issue Purchase Request ID", headerName: "Issue Purchase Request ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
];

// Column definitions for Card Charged Raw Data
const columnDefsCardChargedRawData = [
  { field: "Purchase Request ID", headerName: "Purchase Request ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Real Card Alias", headerName: "Real Card Alias", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Real Card Number", headerName: "Real Card Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Virtual Card Number", headerName: "Virtual Card Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "VCN Expiry", headerName: "VCN Expiry", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Requestor Name", headerName: "Requestor Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Billing Amount", headerName: "Billing Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Billing Currency Code", headerName: "Billing Currency Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Billing Currency Code Description", headerName: "Billing Currency Code Description", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Amount", headerName: "Merchant Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true, formatType:"AMOUNT" },
  { field: "Merchant Currency Code", headerName: "Merchant Currency Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Currency Code Description", headerName: "Merchant Currency Code Description", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Transaction Exchange Rate", headerName: "Transaction Exchange Rate", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "In Control Transaction Date",
    headerName: "In Control Transaction Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["In Control Transaction Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  {
    field: "Transaction Date",
    headerName: "Transaction Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Transaction Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "Transaction Type", headerName: "Transaction Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Transaction Sub Type", headerName: "Transaction Sub Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Transaction Environment", headerName: "Transaction Environment", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Issuer Response", headerName: "Issuer Response", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "In Control Response", headerName: "In Control Response", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "MCC", headerName: "MCC", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "MCC Description", headerName: "MCC Description", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant ID", headerName: "Merchant ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Name", headerName: "Merchant Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant City", headerName: "Merchant City", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant State", headerName: "Merchant State", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Country Code", headerName: "Merchant Country Code", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Merchant Country", headerName: "Merchant Country", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Acquirer ICA", headerName: "Acquirer ICA", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Processor ICA", headerName: "Processor ICA", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Terminal ID", headerName: "Terminal ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Settled", headerName: "Settled", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Authorization Date",
    headerName: "Authorization Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["Authorization Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { 
    field: "Purchase Request Status", 
    headerName: "Purchase Request Status", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    cellRenderer: statusCellRenderer
  },
  { field: "Token PAN", headerName: "Token PAN", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Token Requestor Id", headerName: "Token Requestor Id", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "Token Type", headerName: "Token Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "Enabled for mobile payments",
    headerName: "Enabled for mobile payments",
    filter: "agSetColumnFilter",
    enableRowGroup: true,
    sortable: true,
  
    // Ensure filter sees only "Yes" / "No"
    valueFormatter: (params) => {
      if (params.value === true || params.value === "true" || params.value === 1)
        return "Yes";
      if (params.value === false || params.value === "false" || params.value === 0)
        return "No";
      return "";
    },
  
    // Icon rendering stays visual-only
    cellRenderer: yesNoIconRenderer,
  
    filterParams: {
      values: ["Yes", "No"],   // Explicit set filter values
      suppressMiniFilter: false,
    },
  },
  { field: "Delivery Method", headerName: "Delivery Method", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF1 Label", headerName: "CDF1 Label", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "CDF1 Value", headerName: "CDF1 Value", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
];

// Column definitions for Reservation Data
const columnDefsReservationData = [
  { field: "booking Id", headerName: "Booking ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "vendorReference", headerName: "Vendor Reference", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "supplier Source", headerName: "Supplier Source", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "source Code", headerName: "Source Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "vendor Id", headerName: "Vendor ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "original VendorId", headerName: "Original Vendor ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "isDistress Booking", headerName: "Is Distress Booking", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "room Source", headerName: "Room Source", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "rate Plan Type", headerName: "Rate Plan Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "travel Agent Name", headerName: "Travel Agent Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "booking Date",
    headerName: "Booking Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["booking Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  {
    field: "booking Time",
    headerName: "Booking Time",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (required for agDateColumnFilter)
    valueGetter: (params) => {
      const value = params.data?.["booking Time"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Display as dd-mm-yy
    valueFormatter: (params) => {
      if (!params.value) return "";
  
      const d = params.value;
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = String(d.getFullYear()).slice(-2);
  
      return `${day}/${month}/${year}`;
    },
  
    // 3️⃣ Date filter comparator (time ignored)
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },  
  { field: "navCode", headerName: "NAV Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "check In Date",
    headerName: "Check In Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["check In Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  {
    field: "check Out Date",
    headerName: "Check Out Date",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["check Out Date"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "no of Nights/Hours", headerName: "No of Nights/Hours", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "address", headerName: "Address", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "no of Rooms", headerName: "No of Rooms", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "rooms Type", headerName: "Rooms Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "hotel Name", headerName: "Hotel Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true, formatType:"ENTITY_WITH_LOGO" },
  { field: "original Hotel Name", headerName: "Original Hotel Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "city", headerName: "City", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "guest Name", headerName: "Guest Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "adult / Child", headerName: "Adult / Child", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "childrenAge", headerName: "Children Age", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "status", headerName: "Status", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "confirmed By", headerName: "Confirmed By", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "comments", headerName: "Comments", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "reconfirmed by", headerName: "Reconfirmed By", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "flight Details", headerName: "Flight Details", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "car Details", headerName: "Car Details", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "special Remark", headerName: "Special Remark", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "sld Amend Remark", headerName: "SLD Amend Remark", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "alt YN", headerName: "Alt Y/N", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "amend Vendor Name", headerName: "Amend Vendor Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "user Currency", headerName: "User Currency", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "sell Rate incl hotel taxes", headerName: "Sell Rate incl Hotel Taxes", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "travelGuru Commission", headerName: "TravelGuru Commission", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "net Room Rate(Inc. Taxes)", headerName: "Net Room Rate (Inc. Taxes)", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "actual Amount Paid", headerName: "Actual Amount Paid", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payment Mode", headerName: "Payment Mode", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "cheque Number", headerName: "Cheque Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payment Status", headerName: "Payment Status", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "paymentDetails", headerName: "Payment Details", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "payment Issued On",
    headerName: "Payment Issued On",
    filter: "agDateColumnFilter",
    sortable: true,
    enableRowGroup: true,
  
    // 1️⃣ Convert string → Date (important for filter)
    valueGetter: (params) => {
      const value = params.data?.["payment Issued On"];
      return value ? new Date(value.replace(" ", "T")) : null;
    },
  
    // 2️⃣ Format Date for display
    valueFormatter: (params) => {
      if (!params.value) return "";
      return params.value.toLocaleDateString("en-GB"); // 01/12/2025
    },
  
    // 3️⃣ Ensure date filter compares correctly
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1;
  
        const cellDate = new Date(cellValue);
        cellDate.setHours(0, 0, 0, 0);
  
        if (cellDate < filterLocalDateAtMidnight) return -1;
        if (cellDate > filterLocalDateAtMidnight) return 1;
        return 0;
      },
    },
  },
  { field: "paid in favour of", headerName: "Paid in Favour of", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "accountant Remarks", headerName: "Accountant Remarks", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "bankName", headerName: "Bank Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "account Name", headerName: "Account Name", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "accountNo", headerName: "Account No", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "ifscCode", headerName: "IFSC Code", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "hsEmail", headerName: "HS Email", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "bankEmail", headerName: "Bank Email", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payable to hotel", headerName: "Payable to Hotel", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "plb Type", headerName: "PLB Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "pb ID", headerName: "PB ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "pb RNS Adjusted in this booking", headerName: "PB RNS Adjusted in this Booking", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "pb Amount Adjusted in this booking", headerName: "PB Amount Adjusted in this Booking", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "pb Configured", headerName: "PB Configured", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "inclusions", headerName: "Inclusions", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "yatra Triple Guarantee", headerName: "Yatra Triple Guarantee", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "property Type", headerName: "Property Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "check In Status", headerName: "Check In Status", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { 
    field: "is Book On Request", 
    headerName: "Is Book On Request", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    valueFormatter: booleanValueFormatter,
    cellRenderer: booleanCellRenderer
  },
  { field: "payment Class", headerName: "Payment Class", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  {
    field: "short Stay", 
    headerName: "Short Stay", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    valueFormatter: booleanValueFormatter,
    cellRenderer: booleanCellRenderer
  },
  { field: "authorized Prepay percentage", headerName: "Authorized Prepay Percentage", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "amount Collected from customer", headerName: "Amount Collected from Customer", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "amount due to hotelier from customer", headerName: "Amount Due to Hotelier from Customer", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "amount due to hotel from TG", headerName: "Amount Due to Hotel from TG", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "calling indicator", headerName: "Calling Indicator", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "num of Rooms", headerName: "Num of Rooms", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "phone1", headerName: "Phone 1", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "phone2", headerName: "Phone 2", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "spoc Phone Details", headerName: "SPOC Phone Details", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "cut And Pay Commission", headerName: "Cut And Pay Commission", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "is Commission Collected From Hotelier", headerName: "Is Commission Collected From Hotelier", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "commission Amount Collected From Hotelier", headerName: "Commission Amount Collected From Hotelier", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "date Of Commission Collection", headerName: "Date Of Commission Collection", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "transaction Details Of The Commission Payment", headerName: "Transaction Details Of The Commission Payment", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "gst Registered", headerName: "GST Registered", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "infinity Interaction Id", headerName: "Infinity Interaction ID", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "confirmation -DND", headerName: "Confirmation -DND", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "rate Of Exchange", headerName: "Rate Of Exchange", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "clientEmail", headerName: "Client Email", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "updated in CorpMO(Y/N)", headerName: "Updated in CorpMO (Y/N)", filter: "agDateColumnFilter", enableRowGroup: true, sortable: true },
  { field: "corpMO Remarks", headerName: "CorpMO Remarks", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "business Model", headerName: "Business Model", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "billing Entity", headerName: "Billing Entity", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "is Hourly Booking", headerName: "Is Hourly Booking", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "yatra Smart", headerName: "Yatra Smart", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "yatra Smart Refund", headerName: "Yatra Smart Refund", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "yatra Smart Refund Type", headerName: "Yatra Smart Refund Type", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "yatra Smart Comments", headerName: "Yatra Smart Comments", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "hotelTCS", headerName: "Hotel TCS", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "hotelTDS", headerName: "Hotel TDS", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "customerEmail", headerName: "Customer Email", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "customerPhone", headerName: "Customer Phone", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "hotelNoshow", headerName: "Hotel No Show", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "customerNoShow", headerName: "Customer No Show", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "vendorPaymentModel", headerName: "Vendor Payment Model", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payatHotelComments", headerName: "Pay at Hotel Comments", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "customerPAN", headerName: "Customer PAN", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "tdsApplicable", headerName: "TDS Applicable", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "companyAccount", headerName: "Company Account", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "payLater", headerName: "Pay Later", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "riskFlag", headerName: "Risk Flag", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "customer Pan Card", headerName: "Customer Pan Card", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "tcs Intl", headerName: "TCS Intl", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "card Amount", headerName: "Card Amount", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { 
    field: "card Status", 
    headerName: "Card Status", 
    filter: "agTextColumnFilter",
    enableRowGroup: true, 
    sortable: true,
    valueFormatter: booleanValueFormatter,
    cellRenderer: booleanCellRenderer
  },
  { field: "virtual Credit Card Number", headerName: "Virtual Credit Card Number", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "transaction Number", headerName: "Transaction Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "purchase Request ID", headerName: "Purchase Request ID", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "tG_or_ChannelDiscountAmt", headerName: "TG or Channel Discount Amt", filter: "agNumberColumnFilter", enableRowGroup: true, sortable: true },
  { field: "utrNumber", headerName: "UTR Number", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
  { field: "grossPayout", headerName: "Gross Payout", filter: "agTextColumnFilter", enableRowGroup: true, sortable: true },
];

const items = [
  { key: "Dashboard", label: "Dashboard" },
  { key: "IssuedRawReport", label: "Issued Data" },
  { key: "BookingWise", label: "Booking Wise" },
  { key: "CardChargedRawData", label: "Card charged Data" },
  { key: "ReservationData", label: "Reservation Data" },
];

function VCCNewSection() {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const ref = useRef(null);

  return (
    <>
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* <div
          style={{
            display: "flex",
            gap: "16px",
            marginBottom: "20px",
            marginTop: "20px",
          }}
        >
          <Typography className="reportTxt">Reports</Typography>
        </div> */}
        <PageHeader
          title="VCC"
          leftActions={
            <div style={{ width: "100%", minWidth: "800px" }}>
            <Tabs
              className="my-custom-tabs"
              items={items}
              activeKey={activeTab}
              onChange={setActiveTab}
                moreIcon={null}
                size="small"
                tabBarGutter={16}
              tabBarStyle={{
                borderBottom: "none",
                margin: 0,
                boxShadow: "none",
                  width: "100%",
                }}
                style={{ border: "none", boxShadow: "none", width: "100%" }}
              />
            </div>
          }
          // rightActions={
          //   <Button
          //     size="small"
          //     style={{ backgroundColor: "#0A8394", color: "white" }}
          //   >
          //     Download Report
          //   </Button>
          // }
        />
        <div style={{ flex: 1, marginTop: "16px", height: "100%" }}>
          {activeTab === "Dashboard" ? (
            <div style={{ width: "100%", height: "100%" }}>
              <iframe
                src="https://analytics.zoho.in/open-view/103074000049881898/f9e56c1bdd304cb175682c900b60cf7a3fc44df737b2fff90fb76d390c0fd419"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="VCC Dashboard"
              />
            </div>
          ) : activeTab === "IssuedRawReport" ? (
            <AgTableClient
              columnDefs={columnDefsIssuedRawReport}
              rowData={issuedRawReport}
              ref={ref}
              groupPanel
            />
          ) : activeTab === "BookingWise" ? (
              <AgTableClient
              columnDefs={columnDefsBookingWise}
              rowData={bookingWise}
                ref={ref}
                groupPanel
              />
          ) : activeTab === "CardChargedRawData" ? (
            <AgTableClient
              columnDefs={columnDefsCardChargedRawData}
              rowData={cardChargedRawData}
              ref={ref}
              groupPanel
            />
          ) : activeTab === "ReservationData" ? (
            <AgTableClient
              columnDefs={columnDefsReservationData}
              rowData={reservationData}
              ref={ref}
              groupPanel
            />
          ) : null}
        </div>
      </div>
    </>
  );
}

export default VCCNewSection;
